import { useState, useEffect } from "react"
import PropTypes from "prop-types"

import IngredientSuggestions from "./ingredient_suggestions"
import { ingredientsMetadataShape } from "app/lib/shared_prop_types"
import dataStoreIconRaw from "images/icons/icon-query.svg?raw"

const VARIABLES_GROUP_KEY = "__variables__"

const IngredientSuggestionsByService = ({
  ingredientsMetadata,
  addSuggestion,
  closeSuggestions,
  platformComposer,
  shouldHideSuggestions,
  useNamespacedIngredients,
  handleLastSelectedSuggestion,
  lastSelectedSuggestion,
}) => {
  const [optionsGroup, selectGroup] = useState([])
  const [isDataStoreSelected, setIsDataStoreSelected] = useState(false)

  const { persistedDataStore = [], filterCodeDataStore = [] } = ingredientsMetadata

  // Combine all DataStore keys (persisted + filter code) and deduplicate
  const allDataStoreKeys = [...new Set([...persistedDataStore, ...filterCodeDataStore])]

  const groupSteps = () => {
    const { trigger, queries = [] } = ingredientsMetadata

    return [trigger].concat(queries).reduce((acc, cur) => {
      if (!(cur && cur.service)) return acc
      return {
        ...acc,
        [cur.service.name]: (acc[cur.service.name] || []).concat(cur),
      }
    }, {})
  }

  const stepsByService = groupSteps()

  const groupIngredients = () => {
    return Object.keys(stepsByService).map(service =>
      stepsByService[service]
        .map(s => s.ingredients)
        .reduce((acc, cur) => acc.concat(cur), [])
        // Sub query ingredients are only supported in filter code
        .filter(i => i.value_type !== "query")
        .map(i => {
          return {
            [i.normalized_name.split(".", 2).join(".")]: {
              ...i,
              service: stepsByService[service][0]["service"],
            },
          }
        })
    )
  }

  // Option name will only be action/trigger name.
  const groupOptions = () => {
    const serviceOptions = Object.keys(stepsByService).map((service, i) => (
      <optgroup label={service} key={i}>
        {stepsByService[service]
          // Include friendlier step name in ingredient
          .map(step =>
            step.ingredients.map(ingredient => ({
              ...ingredient,
              stepName: step.name,
            }))
          )
          // flatten
          .reduce((acc, ingredients) => [...acc, ...ingredients], [])
          // array of grouped namespaces, ie "ServiceName.stepName"
          .map(i => ({
            ...i,
            groupedName: i.normalized_name.split(".", 2).join("."),
          }))
          // array of grouped unique namespaces
          .reduce(
            (acc, cur) =>
              acc.some(i => i.groupedName === cur.groupedName)
                ? acc
                : [...acc, cur],
            []
          )
          // build options
          .map((ingredient, idx) => (
            <option key={`ingredient-${idx}`} value={ingredient.groupedName}>
              {ingredient.stepName}
            </option>
          ))}
      </optgroup>
    ))

    if (allDataStoreKeys.length > 0) {
      serviceOptions.push(
        <option key="datastore" value={VARIABLES_GROUP_KEY}>
          DataStore
        </option>
      )
    }

    return serviceOptions
  }

  const options = groupOptions()

  const suggestIngredients = stepNormalizedName => {
    if (stepNormalizedName === VARIABLES_GROUP_KEY) {
      setIsDataStoreSelected(true)
      selectGroup([])
      return
    }

    setIsDataStoreSelected(false)
    const ingredients = groupIngredients().flat()
    const selectedGroup = ingredients
      .map(f => {
        return Object.keys(f).find(o => o === stepNormalizedName)
      })
      .reduce((acc, cur) => {
        return cur ? [...acc, cur] : acc
      }, [])[0]

    selectGroup(
      ingredients
        .filter(i => i.hasOwnProperty(selectedGroup))
        .map(o => o[selectedGroup])
    )
  }

  const renderSelector = () => {
    return <select disabled={options.length < 1}>{options}</select>
  }

  useEffect(() => {
    // build ingredient options for the very first step
    if (lastSelectedSuggestion) {
      suggestIngredients(lastSelectedSuggestion)
    } else {
      const allIngredients = groupIngredients().flat()
      if (allIngredients.length > 0) {
        const firstValue = Object.keys(allIngredients[0])[0]
        handleLastSelectedSuggestion(firstValue)
        suggestIngredients(firstValue)
      } else if (allDataStoreKeys.length > 0) {
        handleLastSelectedSuggestion(VARIABLES_GROUP_KEY)
        suggestIngredients(VARIABLES_GROUP_KEY)
      }
    }
  }, [allDataStoreKeys.length])

  const hasServiceIngredients = optionsGroup.length > 0

  return (
    <>
      {isDataStoreSelected && (
        <IngredientSuggestions
          service={{ name: "DataStore", background_color: "#09f", svgIcon: dataStoreIconRaw }}
          trigger={{ name: "DataStore" }}
          suggestions={allDataStoreKeys.map(key => `DataStore.${key}`)}
          onSelect={fullyQualifiedKey => addSuggestion(fullyQualifiedKey)}
          onClose={closeSuggestions}
          platformComposer={platformComposer}
          selector={renderSelector()}
          onChange={selected => suggestIngredients(selected)}
          lastSelectedSuggestion={lastSelectedSuggestion}
          handleLastSelectedSuggestion={handleLastSelectedSuggestion}
          shouldHideSuggestions={shouldHideSuggestions}
          hideIngredientNamespaces={true}
        />
      )}

      {!isDataStoreSelected && hasServiceIngredients && (
        <IngredientSuggestions
          service={optionsGroup[0].service}
          trigger={optionsGroup[0]}
          suggestions={optionsGroup.map(opt => opt.name)}
          onSelect={selected => addSuggestion(selected)}
          onClose={closeSuggestions}
          platformComposer={platformComposer}
          selector={renderSelector()}
          onChange={selected => suggestIngredients(selected)}
          lastSelectedSuggestion={lastSelectedSuggestion}
          handleLastSelectedSuggestion={handleLastSelectedSuggestion}
          shouldHideSuggestions={shouldHideSuggestions}
          useNamespacedIngredients={useNamespacedIngredients}
        />
      )}
    </>
  )
}

IngredientSuggestionsByService.propTypes = {
  ingredientsMetadata: ingredientsMetadataShape.isRequired,
  addSuggestion: PropTypes.func.isRequired,
  closeSuggestions: PropTypes.func.isRequired,
  platformComposer: PropTypes.bool,
  shouldHideSuggestions: PropTypes.func.isRequired,
  useNamespacedIngredients: PropTypes.bool,
  handleLastSelectedSuggestion: PropTypes.func.isRequired,
  lastSelectedSuggestion: PropTypes.string,
}

export default IngredientSuggestionsByService
